import { useState } from "react";
import {
  Building,
  User,
  Phone,
  MapPin,
  Package,
  Barcode,
  Activity,
  DollarSign,
  CreditCard,
  CalendarCheck,
  CalendarClock,
  FileText,
  Truck,
  UserCheck,
  MessageSquarePlus,
  HelpCircle,
} from "lucide-react";
import { getFileUrl } from "@shared/utils/fileUrl";
import { financial } from "@shared/utils/helper";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";

import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/components/ui/tooltip";
import { STATUS_OPTIONS, STATUS_COLORS } from "@shared/utils/constants";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { printSpeedafLabel } from "../services/packagesService";
import TrackingPackages from "@/features/packages/components/TrackingPackages";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import InvoiceBadge from "./InvoiceBadge";
import CreateTicketDialog from "@features/tickets/CreateTicketDialog";

export const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="grid grid-cols-[24px_1fr_2fr] gap-4 items-center py-2 border-b last:border-b-0">
    <Icon className="w-6 h-6 text-muted-foreground" />
    <div className="font-medium text-sm text-muted-foreground">{label}</div>
    <div className="text-right">{value}</div>
  </div>
);

export function CustomerInfo({ customer }) {
  const { t } = useTranslation();

  return (
    <Card className="w-full mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="w-6 h-6" />
          {t("packages.customerInformation")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid">
          <InfoItem
            icon={User}
            label={t("packages.customerName")}
            value={customer.name}
          />
          <InfoItem
            icon={Phone}
            label={t("packages.customerPhone")}
            value={customer.phone}
          />
          <InfoItem
            icon={Building}
            label={t("packages.customerCity")}
            value={customer.city}
          />
          {customer.secteur && (
            <InfoItem
              icon={MapPin}
              label={t("packages.customerSecteur")}
              value={customer.secteur}
            />
          )}
          <InfoItem
            icon={MapPin}
            label={t("packages.customerAddress")}
            value={customer.address}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function ProductDetails({ products }) {
  const { t } = useTranslation();

  return (
    <Card className="w-full mx-auto">
      <CardHeader>
        <CardTitle className="text-xl font-bold">
          {t("packages.productDetails")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          {products.map((product) => (
            <Card key={product.name} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-3 gap-2 p-4">
                  <div className="md:col-span-3">
                    <h3 className="text-lg font-semibold truncate">
                      {product.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {t("packages.quantity")}
                    </span>
                    <Badge variant="secondary">{product.quantity}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {t("packages.pricePerUnit")}
                    </span>
                    <Badge variant="outline">
                      {product.price.toFixed(2)} {t("main.currency")}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {t("packages.totalAmount")}
                    </span>
                    <Badge variant="default">
                      {(product.price * product.quantity).toFixed(2)}{" "}
                      {t("main.currency")}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function PackageInfo({ packageItem, isAdmin }) {
  const { t } = useTranslation();

  return (
    <Card className="w-full mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-6 h-6" />
          {t("packages.packageInformation")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid ">
          <InfoItem
            icon={Package}
            label={t("packages.id")}
            value={packageItem._id}
          />
          <InfoItem
            icon={Barcode}
            label={t("packages.trackingNumber")}
            value={
              isAdmin && packageItem.trackingNumber
                ? packageItem.trackingNumber
                : packageItem?.trackingNumber?.replace("MA", "SP-") ||
                  packageItem?.trackingNumber
            }
          />
          <InfoItem
            icon={Activity}
            label={t("status.status")}
            value={
              <Badge
                className={`${STATUS_COLORS[packageItem.status]} hover:${
                  STATUS_COLORS[packageItem.status]
                }`}
              >
                {t(STATUS_OPTIONS[packageItem.status])}
              </Badge>
            }
          />
          <InfoItem
            icon={FileText}
            label={t("invoices.invoice")}
            value={<InvoiceBadge pkg={packageItem} />}
          />
          <InfoItem
            icon={DollarSign}
            label={t("packages.totalCost")}
            value={`${financial(packageItem.totalCost)} ${t("main.currency")}`}
          />
          <InfoItem
            icon={CreditCard}
            label={t("packages.totalAmount")}
            value={`${financial(packageItem.totalAmount)} ${t(
              "main.currency"
            )}`}
          />

          <InfoItem
            icon={CalendarClock}
            label={t("common.created")}
            value={format(packageItem.createdAt, "dd/MM/yyyy HH:mm")}
          />
          <InfoItem
            icon={CalendarCheck}
            label={t("common.updated")}
            value={format(packageItem.updatedAt, "dd/MM/yyyy HH:mm")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function UserCard({ user }) {
  const navigate = useNavigate();
  return (
    <Card
      className="w-full mx-auto cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(`/users/${user._id}`)}
    >
      <CardContent className="p-4">
        <div className="flex gap-2 items-center justify-start">
          <Avatar className="size-16 rounded-full">
            <AvatarImage
              src={getFileUrl(user.profileImage)}
              alt={`${user.fullName} profile image`}
            />
            <AvatarFallback className="text-xs">
              {user.brandName?.[0]?.toUpperCase() ||
                user.email?.[0]?.toUpperCase() ||
                "U"}
              {user.brandName?.[1]?.toUpperCase() ||
                user.email?.[1]?.toUpperCase() ||
                "S"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{user.brandName || user.fullName}</p>
            <p className="text-xs text-muted-foreground">{user.phone}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function OwnerInfo({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  return (
    <Card className="w-full mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="w-6 h-6" />
          {t("Package Owner")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {/* User Avatar and Basic Info */}
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <Avatar className="size-16 rounded-full">
              <AvatarImage
                src={getFileUrl(user.profileImage)}
                alt={`${user.fullName} profile image`}
              />
              <AvatarFallback className="text-lg">
                {user.brandName?.[0]?.toUpperCase() ||
                  user.email?.[0]?.toUpperCase() ||
                  "U"}
                {user.brandName?.[1]?.toUpperCase() ||
                  user.email?.[1]?.toUpperCase() ||
                  "S"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-semibold text-lg">
                {user.brandName || user.fullName}
              </p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>

          {/* User Details */}
          <div className="grid gap-2">
            <InfoItem
              icon={User}
              label={t("user.fullName")}
              value={user.fullName}
            />
            <InfoItem icon={Phone} label={t("user.phone")} value={user.phone} />
            {user.city && (
              <InfoItem
                icon={MapPin}
                label={t("user.city")}
                value={user.city}
              />
            )}
            {user.address && (
              <InfoItem
                icon={Building}
                label={t("user.address")}
                value={user.address}
              />
            )}
            {user.website && (
              <InfoItem
                icon={Building}
                label={t("user.website")}
                value={
                  <a
                    href={user.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {user.website}
                  </a>
                }
              />
            )}
          </div>

          {/* View Profile Button */}
          <Button
            variant="outline"
            className="w-full mt-2"
            onClick={() => navigate(`/users/${user._id}`)}
          >
            {t("profile.viewProfile", "View Profile")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function LivreurInfo({ packageItem }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (!packageItem.livreur && user?.role !== "admin") {
    return null; // Don't show for non-admins if no livreur assigned
  }

  return (
    <Card className="w-full mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-6 h-6" />
          {t("Delivery Information")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid">
          {packageItem.livreur ? (
            <>
              <InfoItem
                icon={UserCheck}
                label={t("Assigned Livreur")}
                value={
                  <div className="flex items-center gap-2">
                    <Avatar className="size-8">
                      <AvatarImage
                        src={getFileUrl(packageItem.livreur.profileImage)}
                        alt={`${packageItem.livreur.brandName} profile image`}
                      />
                      <AvatarFallback className="text-xs">
                        {packageItem.livreur.brandName?.[0]?.toUpperCase() ||
                          packageItem.livreur.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <p className="font-medium">
                        {packageItem.livreur.brandName ||
                          packageItem.livreur.email}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {packageItem.livreur.phone}
                      </p>
                    </div>
                  </div>
                }
              />
              {packageItem.assignedAt && (
                <InfoItem
                  icon={CalendarCheck}
                  label={t("Assigned At")}
                  value={format(
                    new Date(packageItem.assignedAt),
                    "dd/MM/yyyy HH:mm"
                  )}
                />
              )}
              <InfoItem
                icon={Activity}
                label={t("Delivery Status")}
                value={
                  <Badge className={STATUS_COLORS[packageItem.status]}>
                    {t(STATUS_OPTIONS[packageItem.status])}
                  </Badge>
                }
              />
            </>
          ) : (
            <InfoItem
              icon={User}
              label={t("Assignment Status")}
              value={
                <Badge
                  variant="outline"
                  className="bg-orange-50 text-orange-700 border-orange-200"
                >
                  {t("Unassigned")}
                </Badge>
              }
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const PackageDetails = ({ packageItem }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [isTicketDialogOpen, setIsTicketDialogOpen] = useState(false);

  const handleTicketSuccess = () => {
    toast.success(
      t("tickets.ticketCreatedSuccess", "Support ticket created successfully!")
    );
    // Optionally navigate to tickets page or refresh
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Enhanced Action Bar */}
        <Card className="border-dashed">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                  <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">
                    {t("packages.needHelp", "Need help with this package?")}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t(
                      "packages.reportIssueDesc",
                      "Report delivery issues, damages, or ask questions about this package"
                    )}
                  </p>
                </div>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => setIsTicketDialogOpen(true)}
                      variant="default"
                      className="gap-2 shrink-0 shadow-sm hover:shadow-md transition-shadow"
                      size="default"
                    >
                      <MessageSquarePlus className="h-4 w-4" />
                      {t("packages.createTicket", "Report Issue")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {t(
                        "packages.createTicketTooltip",
                        "Create a support ticket for this package"
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* <PackageStatusTimeline
        statusHistory={packageItem?.statusHistory}
        customerInfo={packageItem?.customer}
      /> */}

          <div className="grid gap-6 flex-1">
            <PackageInfo packageItem={packageItem} isAdmin={auth.isAdmin} />
            <CustomerInfo customer={packageItem.customer} />
            {/* Show Owner Info for admin, manager, and clientSupport */}
            {(auth.isAdmin || auth.isManager || auth.isClientSupport) &&
              packageItem.user && <OwnerInfo user={packageItem.user} />}
            <LivreurInfo packageItem={packageItem} />
            <ProductDetails products={packageItem.products} />

            <Card className="grid gap-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  {t("packages.note")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-muted p-3 rounded-md">
                  {packageItem.note}
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-col gap-4 min-w-[400px]">
            <div className="px-2 w-full flex flex-col">
              {/* Print Speedaf Label Button */}
              {(auth.isAdmin ||
                auth.isManager ||
                (auth.user && auth.user.role === "client")) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="default"
                        onClick={async () => {
                          try {
                            // show a small toast/feedback
                            toast.promise(
                              (async () => {
                                const blob = await printSpeedafLabel(
                                  packageItem._id
                                );
                                const file = new Blob([blob], {
                                  type: "application/pdf",
                                });
                                const fileURL =
                                  window.URL.createObjectURL(file);
                                window.open(fileURL, "_blank");
                              })(),
                              {
                                loading: t(
                                  "packages.printing",
                                  "Preparing label..."
                                ),
                                success: t("packages.printed", "Label opened"),
                                error: t(
                                  "packages.printFailed",
                                  "Failed to fetch label"
                                ),
                              }
                            );
                          } catch (err) {
                            console.error("Print Speedaf label error:", err);
                            toast.error(
                              err?.response?.data?.message ||
                                err?.message ||
                                t(
                                  "packages.printFailed",
                                  "Failed to fetch label"
                                )
                            );
                          }
                        }}
                        className="gap-2 shrink-0 shadow-sm hover:shadow-md transition-shadow"
                        size="default"
                      >
                        <Barcode className="h-4 w-4" />
                        {t("packages.printLabel", "Print Label")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t(
                          "packages.printLabelTooltip",
                          "Print or preview shipping label"
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <TrackingPackages statusHistory={packageItem?.statusHistory} />
          </div>
        </div>
      </div>

      {/* Create Ticket Dialog */}
      <CreateTicketDialog
        open={isTicketDialogOpen}
        onClose={() => setIsTicketDialogOpen(false)}
        onSuccess={handleTicketSuccess}
        packageData={{
          packageId: packageItem._id,
          trackingNumber: packageItem.trackingNumber,
          status: packageItem.status,
          customer: packageItem.customer,
        }}
      />
    </>
  );
};

export default PackageDetails;
